// A ride is sent to the server as it goes (ADR 0019): the recording is opened when the ride starts, its new samples
// go up every couple of minutes, and the close turns it into an activity with its rewards. A browser that dies at the
// twentieth hour loses minutes, not the day. Network trouble only delays the upload (the next tick, or the outbox at the
// end); a server refusal gives the live path up, and the end of the ride falls back to the whole-activity save.
import { RECORDING_CHUNK, type ActivityMeta, type FtpState, type RecordingState } from '@gruvetto/contracts';
import type { Samples, RewardResult, Summary } from '@gruvetto/core';
import { api, ApiError } from './api.js';
import type { State } from './store.js';

/** Time between two uploads of the new samples. */
export const UPLOAD_EVERY_MS = 120_000;

export const newActivityId = () => 'a' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

/** Response of a save (POST /activities, or the close of a recording). */
export interface ServerActivity {
  id: string;
  date: number;
  type: string;
  name: string;
  summary: Summary;
  xp: number;
  ftp: number;
  mass: number;
  meta: ActivityMeta;
  samples?: Samples;
  replay?: boolean;
}
export type SaveResponse =
  | { duplicate: true; id: string }
  | {
      duplicate?: undefined;
      activity: ServerActivity;
      rewards: Omit<RewardResult, 'state'>;
      progress: State['progress'];
      totals: State['totals'];
      plan: State['plan'];
      /** The FTP after this ride (ADR 0027). */
      ftp: FtpState;
    };

/** The server no longer holds the recording (or refused it): the live path is over, the whole ride goes up at the end. */
export class RecordingLost extends Error {
  constructor(message: string) {
    super(message);
  }
}

const slice = (S: Samples, from: number, to: number): Samples => ({
  t: S.t.slice(from, to),
  p: S.p.slice(from, to),
  c: S.c.slice(from, to),
  hr: S.hr.slice(from, to),
  v: S.v.slice(from, to),
  d: S.d.slice(from, to),
  alt: S.alt.slice(from, to),
});

/**
 * Sends what the server misses of `samples`, in slices, from `sent`; returns how many samples the server holds.
 * A 409 (another tab, an earlier upload the client lost track of) only moves the offset to the server's.
 */
export async function pushSamples(id: string, samples: Samples, sent: number): Promise<number> {
  let at = sent;
  while (at < samples.t.length) {
    const to = Math.min(samples.t.length, at + RECORDING_CHUNK);
    try {
      at = (await api<RecordingState>(`/recordings/${id}/samples`, { method: 'PUT', body: { from: at, samples: slice(samples, at, to) } })).length;
    } catch (e) {
      const length = e instanceof ApiError && e.status === 409 ? (e.body as { length?: number } | undefined)?.length : undefined;
      if (typeof length !== 'number' || length === at) throw e;
      at = length;
    }
  }
  return at;
}

/** A whole recording in one go (outbox): opens or resumes it, sends what the server misses, closes it. */
export async function uploadRecording(id: string, date: number, meta: ActivityMeta, samples: Samples): Promise<SaveResponse> {
  const st = await api<RecordingState>('/recordings', { method: 'POST', body: { id, date, meta } });
  await pushSamples(id, samples, st.length);
  return api<SaveResponse>(`/recordings/${id}/close`, { method: 'POST', body: { meta } });
}

/** The live upload of one ride: reads the recorder's samples, which grow while it runs. */
export class LiveRecording {
  readonly id = newActivityId();
  readonly date = Date.now();
  /** Samples the server holds, whether the recording is open there, whether the server gave it up, an upload in flight. */
  private sent = 0;
  private opened = false;
  private lost = false;
  private busy: Promise<void> | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  constructor(
    private readonly samples: Samples,
    private readonly meta: ActivityMeta,
    private readonly every = UPLOAD_EVERY_MS,
  ) {}

  /** Opens the recording and uploads every `every` ms from now on. */
  start() {
    if (this.timer) return;
    this.timer = setInterval(() => void this.tick(), this.every);
    void this.tick();
  }
  get isLost() {
    return this.lost;
  }

  /** One upload: opens if needed, sends the new samples. Offline waits for the next tick; a refusal gives the live path up. */
  tick(): Promise<void> {
    if (this.busy) return this.busy;
    if (this.lost) return Promise.resolve();
    this.busy = (async () => {
      try {
        if (!this.opened) {
          const st = await api<RecordingState>('/recordings', { method: 'POST', body: { id: this.id, date: this.date, meta: this.meta } });
          this.opened = true;
          this.sent = st.length;
        }
        this.sent = await pushSamples(this.id, this.samples, this.sent);
      } catch (e) {
        if (!(e instanceof ApiError && e.offline)) {
          this.lost = true;
          console.warn('live upload given up', e);
        }
      } finally {
        this.busy = null;
      }
    })();
    return this.busy;
  }

  /**
   * Ends the ride: the last samples go up and the recording becomes an activity. Throws `RecordingLost` when the
   * server gave the recording up, an offline `ApiError` when the network is down (the ride then waits in the outbox).
   */
  async close(meta: ActivityMeta): Promise<SaveResponse> {
    this.stop();
    await this.tick();
    if (this.lost) throw new RecordingLost('recording lost');
    if (!this.opened || this.sent < this.samples.t.length) throw new ApiError(0, 'offline');
    try {
      return await api<SaveResponse>(`/recordings/${this.id}/close`, { method: 'POST', body: { meta } });
    } catch (e) {
      if (e instanceof ApiError && !e.offline) throw new RecordingLost(e.message);
      throw e;
    }
  }

  /** Nothing to keep (ride too short): the server forgets the recording. */
  discard() {
    this.stop();
    this.lost = true;
    if (this.opened) api(`/recordings/${this.id}`, { method: 'DELETE' }).catch(() => {});
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}
